import { api } from "../../api"
import { makeUrlSearchParams } from "../../utils/url"
import { endpoints } from "./endpoints"
import {
  CreateHourReportResponseSchema,
  HourReportDayAuditsResponseSchema,
  HourReportFormOptionsResponseSchema,
  HourReportTaskOptionsResponseSchema,
} from "./schemas"
import type {
  CreateHourReportResponse,
  HourReportDayAuditResponse,
  HourReportFormOptionsResponse,
  HourReportTaskOptionsResponse,
  NewHourReportData,
  ReportedHourResponse,
  HourReportRole,
  HourReportStatus,
} from "./schemas"
import { applyReportedHourOptimisticUpdate, makeReportedHourViewModel } from "./reported-hours"
import type { ReportedHourOptimisticUpdate, ReportedHourViewModel } from "./reported-hours"
import type { ReportedHoursScopeQuery } from "./reported-hours-query"
import { makeHourReportDayAuditViewModel } from "./hour-report-day-audit"
import type { HourReportDayAuditViewModel } from "./hour-report-day-audit"
import { getQueryArgsByEndpoints } from "../../utils/rtk-query-util"

export type { HourReportRole } from "./schemas"

const optimisticallyUpdateReportedHour = (
  id: string,
  update: ReportedHourOptimisticUpdate,
  dispatch: any,
  getState: () => unknown
) => {
  return getQueryArgsByEndpoints(getState, "getReportedHours")
    .filter(({ args }) => args !== undefined)
    .map(({ args }) =>
      dispatch(
        hourReportApi.util.updateQueryData("getReportedHours", args, (draft) => {
          const reportIndex = draft.findIndex((report) => report.id === id)
          if (reportIndex === -1) return

          draft[reportIndex] = applyReportedHourOptimisticUpdate(draft[reportIndex], update)
        })
      )
    )
}

const handleOptimisticHourReportUpdate = async (
  id: string,
  update: ReportedHourOptimisticUpdate,
  dispatch: any,
  getState: () => unknown,
  queryFulfilled: Promise<unknown>
) => {
  const patches = optimisticallyUpdateReportedHour(id, update, dispatch, getState)

  try {
    await queryFulfilled
  } catch {
    patches.forEach((patch) => patch.undo())
  }
}

const successfulHourReportInvalidation = (_result: unknown, error: unknown) => (error ? [] : ["HourReport" as const])

export const hourReportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getReportedHours: builder.query<ReportedHourViewModel[], GetReportedHoursQuery>({
      query: (params) => ({
        method: "GET",
        url: endpoints.reportedHours,
        params: makeUrlSearchParams(params),
      }),
      transformResponse: (response: ReportedHourResponse[]) => {
        return response.map(makeReportedHourViewModel)
      },
      providesTags: ["HourReport"],
    }),
    getHourReportFormOptions: builder.query<HourReportFormOptionsResponse, void>({
      query: () => ({
        method: "GET",
        url: endpoints.hourReportFormOptions,
      }),
      transformResponse: (response) => {
        return HourReportFormOptionsResponseSchema.parse(response)
      },
    }),
    getHourReportTaskOptions: builder.query<HourReportTaskOptionsResponse, string>({
      query: (projectId) => ({
        method: "GET",
        url: endpoints.hourReportTaskOptions,
        params: makeUrlSearchParams({ projectId }),
      }),
      transformResponse: (response) => HourReportTaskOptionsResponseSchema.parse(response),
    }),
    getHourReportDayAudits: builder.query<HourReportDayAuditViewModel[], GetHourReportDayAuditsQuery>({
      query: (params) => ({
        method: "GET",
        url: endpoints.hourReportDayAudits,
        params: makeUrlSearchParams(params),
      }),
      transformResponse: (response) => {
        return HourReportDayAuditsResponseSchema.parse(response).audits.map(makeHourReportDayAuditViewModel)
      },
      providesTags: ["HourReportDayAudit"],
    }),
    createHourReport: builder.mutation<CreateHourReportResponse, NewHourReportData>({
      query: (hourReportData) => ({
        method: "POST",
        url: endpoints.hourReports,
        body: hourReportData,
      }),
      transformResponse: (response) => CreateHourReportResponseSchema.parse(response),
      invalidatesTags: ["HourReport"],
    }),
    updateHourReportInfo: builder.mutation<void, { id: string } & HourReportInfoUpdateData>({
      query: ({ id, ...updateData }) => ({
        method: "PUT",
        url: endpoints.hourReportInfo(id),
        body: updateData,
      }),
      onQueryStarted: ({ id, ...update }, { dispatch, getState, queryFulfilled }) =>
        handleOptimisticHourReportUpdate(id, update, dispatch, getState, queryFulfilled),
      invalidatesTags: successfulHourReportInvalidation,
    }),
    updateHourReportProject: builder.mutation<void, { id: string } & HourReportProjectUpdateData>({
      query: ({ id, ...updateData }) => ({
        method: "PUT",
        url: endpoints.hourReportProject(id),
        body: updateData,
      }),
      onQueryStarted: ({ id, ...update }, { dispatch, getState, queryFulfilled }) =>
        handleOptimisticHourReportUpdate(id, update, dispatch, getState, queryFulfilled),
      invalidatesTags: successfulHourReportInvalidation,
    }),
    updateHourReportTask: builder.mutation<void, { id: string } & HourReportTaskUpdateData>({
      query: ({ id, ...updateData }) => ({
        method: "PUT",
        url: endpoints.hourReportTask(id),
        body: updateData,
      }),
      onQueryStarted: ({ id, ...update }, { dispatch, getState, queryFulfilled }) =>
        handleOptimisticHourReportUpdate(id, update, dispatch, getState, queryFulfilled),
      invalidatesTags: successfulHourReportInvalidation,
    }),
    updateHourReportHours: builder.mutation<void, { id: string } & HourReportHoursUpdateData>({
      query: ({ id, ...updateData }) => ({
        method: "PUT",
        url: endpoints.hourReportHours(id),
        body: updateData,
      }),
      onQueryStarted: ({ id, ...update }, { dispatch, getState, queryFulfilled }) =>
        handleOptimisticHourReportUpdate(id, update, dispatch, getState, queryFulfilled),
      invalidatesTags: successfulHourReportInvalidation,
    }),
    checkHourReport: builder.mutation<void, { id: string } & HourReportCheckData>({
      query: ({ id, ...updateData }) => ({
        method: "PATCH",
        url: endpoints.hourReportCheck(id),
        body: updateData,
      }),
      onQueryStarted: ({ id, ...update }, { dispatch, getState, queryFulfilled }) =>
        handleOptimisticHourReportUpdate(id, update, dispatch, getState, queryFulfilled),
      invalidatesTags: successfulHourReportInvalidation,
    }),
    approveHourReport: builder.mutation<void, { id: string } & HourReportApproveData>({
      query: ({ id, ...updateData }) => ({
        method: "PATCH",
        url: endpoints.hourReportApprove(id),
        body: updateData,
      }),
      onQueryStarted: ({ id, ...update }, { dispatch, getState, queryFulfilled }) =>
        handleOptimisticHourReportUpdate(id, update, dispatch, getState, queryFulfilled),
      invalidatesTags: successfulHourReportInvalidation,
    }),
    performHourReportAdminAction: builder.mutation<void, { id: string } & HourReportAdminActionData>({
      query: ({ id, ...actionData }) => ({
        method: "POST",
        url: endpoints.hourReportAdminActions(id),
        body: actionData,
      }),
      onQueryStarted: ({ id, ...actionData }, { dispatch, getState, queryFulfilled }) => {
        if (actionData.action !== "update") return
        return handleOptimisticHourReportUpdate(id, actionData.changes, dispatch, getState, queryFulfilled)
      },
      invalidatesTags: (_result, error) => (error ? [] : ["HourReport", "HourReportDayAudit"]),
    }),
    performHourReportDayAuditAdminAction: builder.mutation<void, HourReportDayAuditAdminActionData>({
      query: (actionData) => ({
        method: "POST",
        url: endpoints.hourReportDayAuditAdminActions,
        body: actionData,
      }),
      invalidatesTags: ["HourReport", "HourReportDayAudit"],
    }),
    requestHourReportReadyForCheck: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        method: "POST",
        url: endpoints.hourReportRequestReadyForCheck(id),
      }),
    }),
    requestHourReportReadyForApproval: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        method: "POST",
        url: endpoints.hourReportRequestReadyForApproval(id),
      }),
    }),
    setDaysReadyForCheck: builder.mutation<AuditDayStatusUpdateResponse, AuditDayReadyForCheckData>({
      query: (auditData) => ({
        method: "POST",
        url: endpoints.setDaysReadyForCheck,
        body: auditData,
      }),
      invalidatesTags: ["HourReport", "HourReportDayAudit"],
    }),
    setDaysReadyForApproval: builder.mutation<AuditDayStatusUpdateResponse, AuditDayReadyForApprovalData>({
      query: (auditData) => ({
        method: "POST",
        url: endpoints.setDaysReadyForApproval,
        body: auditData,
      }),
      invalidatesTags: ["HourReport", "HourReportDayAudit"],
    }),
    setDaysApproved: builder.mutation<void, AuditDayApprovedData>({
      query: (auditData) => ({
        method: "POST",
        url: endpoints.setDaysApproved,
        body: auditData,
      }),
      invalidatesTags: ["HourReport", "HourReportDayAudit"],
    }),
    deleteHourReport: builder.mutation<void, { id: string; as: HourReportRole }>({
      query: ({ id, as }) => {
        switch (as) {
          case "checker":
            return { method: "DELETE", url: endpoints.hourReportCheck(id) }
          case "approver":
            return { method: "DELETE", url: endpoints.hourReportApprove(id) }
          case "hour-report-admin":
            return {
              method: "POST",
              url: endpoints.hourReportAdminActions(id),
              body: { action: "delete" },
            }
          case "reporter":
            return { method: "DELETE", url: endpoints.hourReport(id) }
        }
      },
      invalidatesTags: ["HourReport"],
    }),
  }),
})

export const {
  useGetReportedHoursQuery,
  useGetHourReportFormOptionsQuery,
  useGetHourReportTaskOptionsQuery,
  useGetHourReportDayAuditsQuery,
  useCreateHourReportMutation,
  useUpdateHourReportInfoMutation,
  useUpdateHourReportProjectMutation,
  useUpdateHourReportTaskMutation,
  useUpdateHourReportHoursMutation,
  useCheckHourReportMutation,
  useApproveHourReportMutation,
  usePerformHourReportAdminActionMutation,
  usePerformHourReportDayAuditAdminActionMutation,
  useRequestHourReportReadyForCheckMutation,
  useRequestHourReportReadyForApprovalMutation,
  useSetDaysReadyForCheckMutation,
  useSetDaysReadyForApprovalMutation,
  useSetDaysApprovedMutation,
  useDeleteHourReportMutation,
} = hourReportApi

export type GetReportedHoursQuery = {
  fromDate: string
  toDate?: string
  status?: HourReportStatus
} & ReportedHoursScopeQuery

export type GetHourReportDayAuditsQuery = {
  membershipId: string
  fromDate: string
  toDate: string
}

export type HourReportInfoUpdateData = { description: string | null } | { status: "open" | "submitted" }
export type HourReportProjectUpdateData = { projectId: string | null }
export type HourReportTaskUpdateData = { taskId: string | null }
export type HourReportHoursUpdateData = { hours: number } | { startTime: string; endTime: string }
export type HourReportCheckData = {
  description?: string | null
  projectId?: string | null
  taskId?: string | null
  hours?: number
  startTime?: string
  endTime?: string
  status?: "submitted" | "checked"
}
export type HourReportApproveData = Omit<HourReportCheckData, "status"> & {
  status?: "checked" | "approved"
}
type HourReportAdminUpdateFields = {
  description?: string | null
  projectId?: string | null
  taskId?: string | null
}
type HourReportAdminUpdateTime =
  | { hours: number; startTime?: never; endTime?: never }
  | { hours?: never; startTime: string; endTime: string }
  | { hours?: never; startTime?: never; endTime?: never }

export type HourReportAdminUpdateChanges = HourReportAdminUpdateFields & HourReportAdminUpdateTime
export type HourReportAdminAction = "submit" | "unsubmit" | "check" | "uncheck" | "approve" | "unapprove" | "delete"
export type HourReportAdminActionData =
  | { action: "update"; changes: HourReportAdminUpdateChanges }
  | { action: HourReportAdminAction }
export type HourReportDayAuditAdminAction = Exclude<HourReportAdminAction, "delete">
export type HourReportDayAuditAdminActionData = {
  membershipId: string
  days: string[]
  action: HourReportDayAuditAdminAction
}
export type CreateHourReportDayAuditsData = { membershipId: string; days: string[] }
export type AuditDayStatusUpdateResponse = {
  notificationSent?: boolean
}
export type AuditDayReadyForCheckData = {
  membershipId: string
  days: string[]
  status: "open" | "submitted"
  notifyChecker?: boolean
}
export type AuditDayReadyForApprovalData = {
  membershipId: string
  days: string[]
  status: "submitted" | "checked"
  notifyApprover?: boolean
}
export type AuditDayApprovedData = {
  membershipId: string
  days: string[]
  status: "checked" | "approved"
}
export type { NewHourReportData }
export type { HourReportDayAuditResponse }
export type { HourReportStatus }
export type { ProjectDetails, ReportedHourResponse, TaskDetails } from "./schemas"
export type { ReportedHourViewModel } from "./reported-hours"
